import {
    getRecordingStepPassed,
    mergeStepRecordingState,
    normalizeInstructionsRecordingMarkers,
    reconcileRecordingPlanInstructions,
    resolveRecordingReplayable,
    resolveRecordingStepStatus,
} from './recordingPlanMarkers';

/**
 * Transform backend testComplete.testPlan into normalized instruction entities.
 */

export function groupTestPlanStepsByInstruction(testPlanSteps = []) {
    const byInstruction = {};
    for (const step of testPlanSteps) {
        const instructionId = step.instruction?.id;
        if (instructionId == null) continue;
        const key = String(instructionId);
        if (!byInstruction[key]) byInstruction[key] = [];
        byInstruction[key].push(step);
    }
    return byInstruction;
}

function buildStepFromTestPlanStep(testPlanStep, index) {
    const status = resolveRecordingStepStatus(testPlanStep);
    const replayable = resolveRecordingReplayable(testPlanStep, status);
    const passed = getRecordingStepPassed(testPlanStep);
    return {
        id: testPlanStep.id ?? `step-${testPlanStep.stepIndex ?? index}`,
        stepId: testPlanStep.stepId ?? testPlanStep.id,
        stepIndex: testPlanStep.stepIndex ?? index,
        description: testPlanStep.description,
        tool: testPlanStep.tool,
        args: testPlanStep.args ?? testPlanStep.arguments ?? {},
        customStep: testPlanStep.customStep ?? false,
        status,
        ...(typeof replayable === 'boolean' && { replayable }),
        ...(typeof passed === 'boolean' && { passed }),
        executionStrategies: testPlanStep.executionStrategies,
    };
}

/**
 * Merge executionStrategies and stable stepId from testPlan into existing instructions.
 */
export function mergeTestPlanIntoInstructions(instructions = [], testPlan) {
    const planSteps = testPlan?.steps;
    if (!Array.isArray(planSteps) || planSteps.length === 0) {
        return instructions;
    }

    const testPlanStepsByInstruction = groupTestPlanStepsByInstruction(planSteps);

    const merged = instructions.map((instruction) => {
        const testPlanStepsForInst = testPlanStepsByInstruction[String(instruction.id)];
        if (!testPlanStepsForInst?.length) return instruction;

        if (!instruction.aiSteps?.length) {
            const aiSteps = testPlanStepsForInst.map(buildStepFromTestPlanStep);
            return { ...instruction, aiSteps };
        }

        const updatedSteps = instruction.aiSteps.map((step, index) => {
            const testPlanStep = testPlanStepsForInst[index];
            const planStepId = testPlanStep?.stepId ?? testPlanStep?.id;

            let mergedStep = mergeStepRecordingState(step, testPlanStep ?? {});
            if (typeof planStepId === 'string' && planStepId.trim()) {
                mergedStep = { ...mergedStep, stepId: planStepId.trim() };
            }
            return mergedStep;
        });

        return { ...instruction, aiSteps: updatedSteps };
    });

    let globalIdx = 0;
    return merged.map((inst) => ({
        ...inst,
        aiSteps: (inst.aiSteps ?? []).map((s) => ({ ...s, stepIndex: globalIdx++ })),
    }));
}

export function buildInstructionsFromTestPlan(testPlan, fallbackInstructions = []) {
    if (!testPlan?.steps?.length) return fallbackInstructions;
    if (fallbackInstructions.length > 0) {
        return mergeTestPlanIntoInstructions(fallbackInstructions, testPlan);
    }

    const byInstruction = {};
    for (const step of testPlan.steps) {
        const inst = step.instruction ?? { id: step.instructionId ?? 'inst-1', text: step.instructionText ?? '' };
        const key = String(inst.id);
        if (!byInstruction[key]) {
            byInstruction[key] = { ...inst, aiSteps: [] };
        }
        byInstruction[key].aiSteps.push(buildStepFromTestPlanStep(step));
    }

    let globalIdx = 0;
    return Object.values(byInstruction).map((inst) => ({
        ...inst,
        aiSteps: inst.aiSteps.map((s) => ({ ...s, stepIndex: globalIdx++ })),
    }));
}
